import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { informeService } from '../services/api';
import { InformePericial, FaseInforme, EstadoCita } from '../types';
import { showApiError } from '../utils/toast';
import EncargoForm from './phases/EncargoForm';
import CitaForm from './phases/CitaForm';
import VideoperitacionUpload from './phases/VideoperitacionUpload';
import SubidaArchivosFase from './phases/SubidaArchivosFase';
import GrabacionPeritoFase from './phases/GrabacionPeritoFase';
import PrevisualizacionContextoFase from './phases/PrevisualizacionContextoFase';
import ValoracionFase from './phases/ValoracionFase';
import InformeViewer from './phases/InformeViewer';
import EnviarInforme from './phases/EnviarInforme';
import CerradoInforme from './phases/CerradoInforme';
import { FiEdit, FiCalendar, FiVideo, FiFileText, FiMail, FiLock, FiArchive, FiMic, FiEye } from 'react-icons/fi';
import { FaEuroSign } from 'react-icons/fa';
import './InformeLayout.css';

const PHASES: { id: FaseInforme; label: string; icon: React.ReactNode }[] = [
    { id: 'encargo', label: 'Datos Encargo', icon: <FiEdit /> },
    { id: 'cita', label: 'Cita', icon: <FiCalendar /> },
    { id: 'videoperitacion', label: 'Videoperitación', icon: <FiVideo /> },
    { id: 'subidaArchivos', label: 'Archivos', icon: <FiArchive /> },
    { id: 'grabacion', label: 'Grabación Perito', icon: <FiMic /> },
    { id: 'previsualizacionContexto', label: 'Previsualización Contexto', icon: <FiEye /> },
    { id: 'valoracion', label: 'Valoración', icon: <FaEuroSign /> },
    { id: 'informe', label: 'Informe IA', icon: <FiFileText /> },
    { id: 'envio', label: 'Enviar', icon: <FiMail /> },
    { id: 'cerrado', label: 'Cerrado', icon: <FiLock /> }
];

const InformeLayout: React.FC = () => {
    const { id, fase } = useParams<{ id: string; fase: string }>();
    const navigate = useNavigate();
    const [informe, setInforme] = useState<InformePericial | null>(null);
    const [loading, setLoading] = useState<boolean>(!!id && id !== 'new');
    const [error, setError] = useState<string>('');

    const isNew = id === 'new';
    const currentFase = (fase as FaseInforme) || 'encargo';

    useEffect(() => {
        if (!isNew && id) {
            prvCargarInforme(parseInt(id));
        }
    }, [id]);

    const prvCargarInforme = async (informeId: number) => {
        try {
            setLoading(true);
            const response = await informeService.obtenerInforme(informeId);
            setInforme(response.data);
        } catch (err: any) {
            setError(err.response?.data?.error || 'Error al cargar el informe. Puede que no exista o no tengas permisos.');
            console.error('Error cargando informe:', err);
        } finally {
            setLoading(false);
        }
    };

    const prvValidarAccesoFase = async (targetFase: FaseInforme, targetInforme?: InformePericial): Promise<boolean> => {
        const targetIndex = PHASES.findIndex(p => p.id === targetFase);
        if (targetIndex < 2) {
            return true;
        }

        const inf = targetInforme || informe;
        if (!inf || !inf.id) {
            showApiError("Debes guardar primero los detalles del encargo.");
            return false;
        }

        if (!inf.numero_expediente || !inf.numero_siniestro) {
            showApiError("Los detalles del encargo deben estar completos antes de avanzar.");
            return false;
        }

        try {
            const citaRes = await informeService.obtenerCita(inf.id);
            const citaData = citaRes.data;
            if (!citaData || Number(citaData.estado) !== EstadoCita.CONFIRMAR) {
                showApiError("Para acceder a la videoperitación y fases posteriores, la cita debe estar guardada y en estado 'Confirmado'.");
                return false;
            }
        } catch (err: any) {
            showApiError("La cita debe estar guardada y en estado 'Confirmado' para acceder a esta fase.");
            return false;
        }

        return true;
    };

    useEffect(() => {
        if (informe && !loading && !isNew) {
            const targetIndex = PHASES.findIndex(p => p.id === currentFase);
            if (targetIndex >= 2) {
                prvValidarAccesoFase(currentFase).then(ok => {
                    if (!ok) {
                        navigate(`/informes/${informe.id}/cita`, { replace: true });
                    }
                });
            }
        }
    }, [currentFase, informe, loading, isNew]);

    const prvHandleFaseChange = async (newFase: FaseInforme) => {
        if (isNew || !informe) return;
        const puedePasar = await prvValidarAccesoFase(newFase);
        if (puedePasar) {
            navigate(`/informes/${informe.id}/${newFase}`);
        }
    };

    const prvHandleInformeUpdate = async (updatedInforme: InformePericial, targetFase?: FaseInforme) => {
        setInforme(updatedInforme);
        if (targetFase) {
            const puedePasar = await prvValidarAccesoFase(targetFase, updatedInforme);
            if (puedePasar) {
                navigate(`/informes/${updatedInforme.id}/${targetFase}`);
            }
        } else if (isNew) {
            navigate(`/informes/${updatedInforme.id}/cita`, { replace: true });
        }
    };

    const prvRenderPhaseComponent = () => {
        if (loading) return <div className="loading-spinner">Cargando datos...</div>;
        if (error) return <div className="error-alert">{error}</div>;

        const commonProps = {
            informe: isNew ? undefined : informe || undefined,
            onUpdate: prvHandleInformeUpdate
        };

        switch (currentFase) {
            case 'encargo':
                return <EncargoForm {...commonProps} isNew={isNew} />;
            case 'cita':
                return <CitaForm {...commonProps} />;
            case 'videoperitacion':
                return <VideoperitacionUpload {...commonProps} />;
            case 'subidaArchivos':
                return <SubidaArchivosFase {...commonProps} />;
            case 'grabacion':
                return <GrabacionPeritoFase {...commonProps} />;
            case 'previsualizacionContexto':
                return <PrevisualizacionContextoFase {...commonProps} />;
            case 'valoracion':
                return <ValoracionFase {...commonProps} />;
            case 'informe':
                return <InformeViewer {...commonProps} />;
            case 'envio':
                return <EnviarInforme {...commonProps} />;
            case 'cerrado':
                return <CerradoInforme {...commonProps} />;
            default:
                return <div>Fase no encontrada</div>;
        }
    };

    return (
        <div className="informe-layout">
            <div className="phase-stepper">
                {PHASES.map((p, index) => {
                    const isActive = p.id === currentFase;
                    const isCompleted = informe ? PHASES.findIndex(ph => ph.id === informe.fase) > index : false;
                    const isClickable = !isNew && informe;

                    return (
                        <div
                            key={p.id}
                            className={`step-item ${isActive ? 'active' : ''} ${isCompleted ? 'completed' : ''} ${!isClickable ? 'disabled' : ''}`}
                            onClick={() => isClickable && prvHandleFaseChange(p.id)}
                            data-tooltip={p.label}
                        >
                            <div className="step-icon">{p.icon}</div>
                            {index < PHASES.length - 1 && <div className="step-connector" />}
                        </div>
                    );
                })}
            </div>

            <div className="phase-content">
                {prvRenderPhaseComponent()}
            </div>
        </div>
    );
};

export default InformeLayout;
